import { message } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import { useCallback, useEffect, useState } from 'react'
import type { RuleGroupType } from 'react-querybuilder'
import { formatQuery } from 'react-querybuilder'
import { parseJsonLogic } from 'react-querybuilder/parseJsonLogic'

const DEFAULT_QUERY: RuleGroupType = {
  combinator: 'and',
  rules: [],
}

export interface SaveTemplateParams {
  templateName: string
  description?: string
}

export interface EligibleRuleTemplateData {
  id: string
  clientPublicKey: string
  templateName: string
  description?: string
  versionNumber: number
  ruleDefinition: Record<string, unknown>
  status: string
  createdBy: string
  createdByName?: string
  createdAt: string
  updatedAt: string
}

export default function useEligibleRules(
  customerId: string,
  templateId?: string
) {
  const [query, setQuery] = useState<RuleGroupType>(DEFAULT_QUERY)
  const [isSaving, setIsSaving] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')

  const { data: sourceTemplate, isPending: isLoadingTemplate } =
    useQuery<EligibleRuleTemplateData>({
      queryKey: ['eligible-rule-template', templateId],
      queryFn: async () => {
        const result = await epsClient.get(
          `/api/v1/eligible-rule-templates/${templateId}`
        )
        return result.data
      },
      enabled: !!templateId,
    })

  useEffect(() => {
    if (!sourceTemplate) return
    if (sourceTemplate.templateName)
      setTemplateName(sourceTemplate.templateName)
    if (sourceTemplate.description)
      setTemplateDescription(sourceTemplate.description)
    if (sourceTemplate.ruleDefinition) {
      try {
        const parsed = parseJsonLogic(sourceTemplate.ruleDefinition)
        setQuery(parsed as RuleGroupType)
      } catch {
        // ruleDefinition may already be in RuleGroupType format
        setQuery(sourceTemplate.ruleDefinition as unknown as RuleGroupType)
      }
    }
  }, [sourceTemplate])

  const saveDraft = useCallback(
    async ({ templateName: name, description }: SaveTemplateParams) => {
      const ruleDefinition = formatQuery(query, 'jsonlogic')
      setIsSaving(true)
      try {
        await epsClientV1.post('/eligible-rule-templates', {
          clientPublicKey: customerId,
          templateName: name,
          description,
          ruleDefinition,
        })
        message.success(`Draft "${name}" saved successfully`)
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ?? err?.message ?? 'Unknown error'
        message.error(`Failed to save draft: ${detail}`)
        throw err
      } finally {
        setIsSaving(false)
      }
    },
    [query, customerId]
  )

  const saveAndPublish = useCallback(
    async ({ templateName: name, description }: SaveTemplateParams) => {
      const ruleDefinition = formatQuery(query, 'jsonlogic')
      setIsSaving(true)
      try {
        const draftResponse = await epsClientV1.post(
          '/eligible-rule-templates',
          {
            clientPublicKey: customerId,
            templateName: name,
            description,
            ruleDefinition,
          }
        )
        const newTemplateId = draftResponse.data.id
        await epsClientV1.patch(
          `/eligible-rule-templates/${newTemplateId}/status`,
          { action: 'publish' }
        )
        message.success(
          `Eligible rule template "${name}" saved & published successfully`
        )
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ?? err?.message ?? 'Unknown error'
        message.error(`Failed to save & publish template: ${detail}`)
        throw err
      } finally {
        setIsSaving(false)
      }
    },
    [query, customerId]
  )

  const saveEditDraft = useCallback(
    async ({ templateName: name, description }: SaveTemplateParams) => {
      if (!templateId) return
      const ruleDefinition = formatQuery(query, 'jsonlogic')
      setIsSaving(true)
      try {
        await epsClientV1.patch(`/eligible-rule-templates/${templateId}`, {
          clientPublicKey: customerId,
          templateName: name,
          description,
          ruleDefinition,
        })
        message.success(`Draft "${name}" saved successfully`)
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ?? err?.message ?? 'Unknown error'
        message.error(`Failed to save draft: ${detail}`)
        throw err
      } finally {
        setIsSaving(false)
      }
    },
    [query, customerId, templateId]
  )

  const publishEdit = useCallback(
    async ({ templateName: name, description }: SaveTemplateParams) => {
      if (!templateId) return
      const ruleDefinition = formatQuery(query, 'jsonlogic')
      setIsSaving(true)
      try {
        await epsClientV1.patch(`/eligible-rule-templates/${templateId}`, {
          clientPublicKey: customerId,
          templateName: name,
          description,
          ruleDefinition,
        })
        await epsClientV1.patch(
          `/eligible-rule-templates/${templateId}/status`,
          { action: 'publish' }
        )
        message.success(
          `Eligible rule template "${name}" saved & published successfully`
        )
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ?? err?.message ?? 'Unknown error'
        message.error(`Failed to save & publish template: ${detail}`)
        throw err
      } finally {
        setIsSaving(false)
      }
    },
    [query, customerId, templateId]
  )

  const resetRules = useCallback(() => {
    setQuery(DEFAULT_QUERY)
  }, [])

  return {
    query,
    setQuery,
    saveDraft,
    saveAndPublish,
    saveEditDraft,
    publishEdit,
    resetRules,
    isSaving,
    templateName,
    setTemplateName,
    templateDescription,
    setTemplateDescription,
    sourceTemplate,
    isLoadingTemplate,
  }
}
